/* BillingPage — Live View synced with SmartBill API.
 *
 * Architecture (per 2026-05-26 refactor):
 *   - SmartBill API does NOT support listing invoices. Therefore the corpus
 *     comes from XLSX exports the user uploads (Rapoarte → Facturi → Export).
 *   - Live payment status is fetched per-invoice via `GET /invoice/paymentstatus`
 *     (Refresh button + auto on mount). Bulk refresh is rate-limited server-side
 *     to honour SmartBill's 30 req / 10 s limit.
 *   - Invoices are NOT emitted from the HUB anymore. This module is strictly
 *     read + reconcile.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Receipt, Search, Download, X, FileText, ExternalLink, RefreshCw, Wallet,
  ShoppingBag, Clock, FileX, Upload, Truck, Loader2, CheckCircle2, AlertCircle, Info,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  listInvoices, getBillingKpis, getInvoice, fetchInvoicePdfBlob,
  getBillingSettings, refreshAllInvoices, refreshInvoiceStatus, smartbillWebUrl,
} from "@/lib/billing";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useRealtimeEvent } from "@/lib/realtime-context";
import { useAuth } from "@/lib/auth";

const ron = (n, c = "RON") => `${Number(n || 0).toLocaleString("ro-RO", { maximumFractionDigits: 2 })} ${c}`;

const STATUS_BADGE = {
  issued:    { label: "Emisă",      class: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  paid:      { label: "Achitată",   class: "text-violet-700 bg-violet-50 border-violet-200" },
  cancelled: { label: "Anulată",    class: "text-rose-700 bg-rose-50 border-rose-200" },
  draft:     { label: "Draft",      class: "text-zinc-700 bg-zinc-100 border-zinc-200" },
};
const PAY_BADGE = {
  paid:            { label: "Achitată",     class: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  partially_paid:  { label: "Parțial",      class: "text-amber-700 bg-amber-50 border-amber-200" },
  unpaid:          { label: "Neachitată",   class: "text-rose-700 bg-rose-50 border-rose-200" },
};
const DOC_BADGE = {
  factura:  { label: "Factură",  class: "text-primary border-primary/30 bg-primary/5" },
  proforma: { label: "Proformă", class: "text-blue-700 border-blue-200 bg-blue-50" },
  aviz:     { label: "Aviz",     class: "text-zinc-700 border-zinc-200 bg-zinc-50" },
  storno:   { label: "Storno",   class: "text-rose-700 border-rose-200 bg-rose-50" },
};
const DELIVERY_BADGE = {
  delivered:   { label: "Livrată",      class: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  in_transit:  { label: "În tranzit",   class: "text-blue-700 bg-blue-50 border-blue-200" },
  returned:    { label: "Returnată",    class: "text-rose-700 bg-rose-50 border-rose-200" },
  ready:       { label: "Pregătită",    class: "text-zinc-700 bg-zinc-50 border-zinc-200" },
  cancelled:   { label: "Anulată",      class: "text-rose-700 bg-rose-50 border-rose-200" },
};

export default function BillingPage() {
  const { hasPermission } = useAuth();
  const canView = hasPermission("billing.view");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [payFilter, setPayFilter] = useState("all");
  const [docFilter, setDocFilter] = useState("all");
  const [kpis, setKpis] = useState({});
  const [selected, setSelected] = useState(null);
  const [configured, setConfigured] = useState(true);
  const autoRefreshRan = useRef(false);

  const load = useCallback(async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (payFilter !== "all") params.payment_status = payFilter;
      if (docFilter !== "all") params.document_type = docFilter;
      if (search.trim()) params.search = search.trim();
      const [list, k, cfg] = await Promise.all([
        listInvoices(params), getBillingKpis(), getBillingSettings(),
      ]);
      setItems(list.items || []);
      setKpis(k);
      setConfigured(cfg.configured);
    } catch {
      toast.error("Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  }, [canView, search, statusFilter, payFilter, docFilter]);

  useEffect(() => {
    const t = setTimeout(load, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  // Auto live-refresh from SmartBill API on first mount (after the first load).
  useEffect(() => {
    if (autoRefreshRan.current || !canView || loading) return;
    if (items.length === 0) return;
    autoRefreshRan.current = true;
    (async () => {
      try {
        setRefreshing(true);
        const r = await refreshAllInvoices(50);
        if (r.refreshed > 0) {
          toast.success(`Status SmartBill sincronizat pentru ${r.refreshed} factur${r.refreshed === 1 ? "ă" : "i"}`);
          await load();
        }
      } catch { /* silent on auto refresh */ }
      finally { setRefreshing(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, items.length, canView]);

  useRealtimeEvent("accounting.smartbill_imported", load);

  const manualRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await refreshAllInvoices(200);
      if (r.refreshed > 0) {
        toast.success(`${r.refreshed} actualizate · ${r.failed.length} erori`);
      } else if (r.failed.length > 0) {
        toast.error(`${r.failed.length} erori (vezi consolă)`);
        console.warn("[billing.refresh-all] failed:", r.failed);
      } else {
        toast.message("Nimic de sincronizat");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh eșuat");
    } finally { setRefreshing(false); }
  };

  if (!canView) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="billing-forbidden">
        <Card className="p-8 text-center space-y-3">
          <Receipt className="h-12 w-12 mx-auto text-zinc-400" />
          <h2 className="font-display text-2xl font-bold">Fără acces</h2>
          <p className="text-muted-foreground text-sm">Nu ai permisiunea <code className="font-mono text-xs">billing.view</code>. Contactează un administrator.</p>
        </Card>
      </div>
    );
  }

  if (!configured) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card className="p-8 text-center space-y-4">
          <Receipt className="h-12 w-12 mx-auto text-amber-500" />
          <h2 className="font-display text-2xl font-bold">SmartBill — neconfigurat</h2>
          <p className="text-muted-foreground">Pentru a sincroniza facturile live, conectează contul SmartBill (CIF + token API) în Setări.</p>
          <Link to="/setari/facturare">
            <Button data-testid="goto-billing-settings">Mergi la Setări → Facturare</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-2">
            <Receipt className="h-3 w-3" /> Hub financiar
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Facturare · SmartBill</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Live view sincronizat cu API-ul SmartBill. Status încasare actualizat la cerere.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={manualRefresh}
            disabled={refreshing}
            data-testid="billing-refresh-btn"
          >
            {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            REFRESH din SmartBill
          </Button>
          <Link to="/setari/facturare">
            <Button variant="outline" size="sm" data-testid="billing-settings-link">Setări</Button>
          </Link>
        </div>
      </header>

      {/* Informational banner about the architecture */}
      <Card className="p-3 bg-blue-50/60 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900 flex items-start gap-2.5" data-testid="billing-info">
        <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-900 dark:text-blue-200 space-y-0.5">
          <p>
            <strong>Cum funcționează:</strong> SmartBill API nu permite listarea automată a facturilor. Pentru a popula HUB-ul, importă periodic exportul XLSX din SmartBill
            (<em>Rapoarte → Facturi → Export Excel</em>). Apoi „REFRESH din SmartBill" sincronizează live statusul de încasare pentru fiecare factură via API.
          </p>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiBox label="Emise azi"     value={kpis.issued_today} icon={Receipt}    accent="primary" testid="kpi-billing-issued" />
        <KpiBox label="Valoare azi"   value={kpis.today_gross}  icon={Wallet}     accent="emerald" format="money" testid="kpi-billing-gross" />
        <KpiBox label="TVA colectat"  value={kpis.today_vat}    icon={ShoppingBag} accent="violet" format="money" testid="kpi-billing-vat" />
        <KpiBox label="Neachitate"    value={kpis.unpaid}       icon={Clock}      accent="amber"  testid="kpi-billing-unpaid" />
        <KpiBox label="Anulate"       value={kpis.cancelled}    icon={FileX}      accent="rose"   testid="kpi-billing-cancelled" />
      </div>

      {/* XLSX import zone */}
      <XlsxImportZone onImported={load} />

      {/* Filters */}
      <Card className="p-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută factură / serie / comandă / client / email…"
            className="pl-8 h-9"
            data-testid="billing-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-32" data-testid="billing-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Status: toate</SelectItem>
            <SelectItem value="issued">Emise</SelectItem>
            <SelectItem value="cancelled">Anulate</SelectItem>
          </SelectContent>
        </Select>
        <Select value={payFilter} onValueChange={setPayFilter}>
          <SelectTrigger className="h-9 w-36" data-testid="billing-pay-filter"><SelectValue placeholder="Încasare" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Încasare: toate</SelectItem>
            <SelectItem value="paid">Achitate</SelectItem>
            <SelectItem value="partially_paid">Parțial</SelectItem>
            <SelectItem value="unpaid">Neachitate</SelectItem>
          </SelectContent>
        </Select>
        <Select value={docFilter} onValueChange={setDocFilter}>
          <SelectTrigger className="h-9 w-32" data-testid="billing-doc-filter"><SelectValue placeholder="Tip doc" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tip: toate</SelectItem>
            <SelectItem value="factura">Factură</SelectItem>
            <SelectItem value="proforma">Proformă</SelectItem>
            <SelectItem value="aviz">Aviz</SelectItem>
            <SelectItem value="storno">Storno</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 border-b border-border">
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                <th className="px-3 py-3">Document</th>
                <th className="px-3 py-3">Comandă</th>
                <th className="px-3 py-3">Client</th>
                <th className="px-3 py-3">Dată</th>
                <th className="px-3 py-3 text-right">Total</th>
                <th className="px-3 py-3 text-right">TVA</th>
                <th className="px-3 py-3">Încasare</th>
                <th className="px-3 py-3">AWB</th>
                <th className="px-3 py-3">Livrare</th>
                <th className="px-3 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}><td colSpan={10} className="px-3 py-3"><Skeleton className="h-5 w-full" /></td></tr>
              )) : items.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-12 text-center">
                  <div className="space-y-2 text-muted-foreground">
                    <FileX className="h-8 w-8 mx-auto" />
                    <p className="text-sm">Nicio factură importată încă.</p>
                    <p className="text-xs">Trage exportul XLSX din SmartBill (<em>Rapoarte → Facturi → Export Excel</em>) în zona de upload de mai sus pentru a popula tabelul.</p>
                  </div>
                </td></tr>
              ) : items.map((it) => {
                const ds = DOC_BADGE[it.document_type] || DOC_BADGE.factura;
                const ss = STATUS_BADGE[it.status] || STATUS_BADGE.issued;
                const ps = PAY_BADGE[it.payment_status] || PAY_BADGE.unpaid;
                const ds2 = DELIVERY_BADGE[it.delivery_status];
                return (
                  <tr key={it.id} className="hover:bg-secondary/30 cursor-pointer" onClick={() => openDetail(it.id, setSelected)} data-testid={`invoice-row-${it.id}`}>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`text-[10px] ${ds.class}`}>{ds.label}</Badge>
                        <span className="font-mono text-xs font-semibold">{it.series_name}{it.number}</span>
                        {it.status === "cancelled" && <Badge variant="outline" className={`text-[10px] ${ss.class}`}>{ss.label}</Badge>}
                      </div>
                    </td>
                    <td className="px-3 py-3 font-mono text-xs">{it.shopify_order_number ? `#${it.shopify_order_number}` : "—"}</td>
                    <td className="px-3 py-3 truncate max-w-[200px]">
                      <div className="text-sm truncate">{it.customer_name}</div>
                      {it.customer_email && <div className="text-[10px] text-muted-foreground truncate">{it.customer_email}</div>}
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(it.issue_date || it.created_at)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold whitespace-nowrap">{ron(it.total_gross, it.currency)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-muted-foreground whitespace-nowrap">{ron(it.total_vat, it.currency)}</td>
                    <td className="px-3 py-3">
                      <Badge variant="outline" className={`text-[10px] ${ps.class}`}>{ps.label}</Badge>
                      {it.smartbill_last_synced_at && (
                        <div className="text-[9px] text-muted-foreground mt-0.5">sync {fmtDate(it.smartbill_last_synced_at)}</div>
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono text-[11px]">{it.awb || "—"}</td>
                    <td className="px-3 py-3">
                      {ds2 ? <Badge variant="outline" className={`text-[10px] ${ds2.class}`}>{ds2.label}</Badge> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <a
                        href={smartbillWebUrl(it)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Deschide în SmartBill"
                        data-testid={`invoice-smartbill-link-${it.id}`}
                        className="inline-flex items-center text-muted-foreground hover:text-primary"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <InvoiceDetailDrawer invoice={selected} onOpenChange={(o) => !o && setSelected(null)} onChanged={load} />
    </div>
  );
}

function KpiBox({ label, value, icon: Icon, accent, format, testid }) {
  const formatted = format === "money"
    ? `${Number(value || 0).toLocaleString("ro-RO", { maximumFractionDigits: 0 })} RON`
    : Number(value || 0).toLocaleString("ro-RO");
  const colors = {
    primary: "bg-primary/10 text-primary",
    emerald: "bg-emerald-500/10 text-emerald-600",
    amber: "bg-amber-500/10 text-amber-600",
    rose: "bg-rose-500/10 text-rose-600",
    violet: "bg-violet-500/10 text-violet-600",
  };
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`h-7 w-7 rounded-md flex items-center justify-center ${colors[accent] || colors.primary}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <div className="mt-2 font-display text-2xl font-bold tracking-tight tabular-nums">{formatted}</div>
    </div>
  );
}

function XlsxImportZone({ onImported }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error("Doar fișiere .xlsx / .xls sunt acceptate");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/accounting/smartbill-imports", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const matched = data?.matched ?? data?.summary?.matched ?? 0;
      const added = data?.added ?? data?.summary?.added ?? data?.summary?.inserted ?? 0;
      toast.success(`Import OK · ${added} adăugate · ${matched} corelate cu comenzi Shopify`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import eșuat");
    } finally { setBusy(false); }
  };

  return (
    <Card
      className={`p-4 border-2 border-dashed transition ${drag ? "border-primary bg-primary/5" : "border-border bg-secondary/20"}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer?.files?.[0]); }}
      data-testid="billing-xlsx-zone"
    >
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Upload className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Importă facturi din SmartBill</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Trage aici fișierul exportat din <em>SmartBill → Rapoarte → Facturi → Export Excel</em>, sau click pentru a alege.
            După import, butonul <strong>REFRESH</strong> sincronizează statusul de încasare live via API.
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="billing-xlsx-input"
        />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="billing-xlsx-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
          Alege fișier XLSX
        </Button>
      </div>
    </Card>
  );
}

async function openDetail(id, setSelected) {
  try {
    const inv = await getInvoice(id);
    setSelected(inv);
  } catch { toast.error("Nu pot deschide factura"); }
}

function InvoiceDetailDrawer({ invoice, onOpenChange, onChanged }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [latest, setLatest] = useState(invoice);

  useEffect(() => { setLatest(invoice); }, [invoice?.id]); // eslint-disable-line

  useEffect(() => {
    if (!invoice) { setPdfUrl(null); return; }
    let alive = true;
    setPdfLoading(true);
    fetchInvoicePdfBlob(invoice.id)
      .then((blob) => alive && setPdfUrl(URL.createObjectURL(blob)))
      .catch(() => alive && setPdfUrl(null))
      .finally(() => alive && setPdfLoading(false));
    return () => { alive = false; if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id]);

  if (!invoice) return null;

  const refresh = async () => {
    setRefreshing(true);
    try {
      const updated = await refreshInvoiceStatus(invoice.id);
      setLatest(updated);
      toast.success("Status sincronizat din SmartBill");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare refresh");
    } finally { setRefreshing(false); }
  };

  const inv = latest || invoice;
  const ss = STATUS_BADGE[inv.status] || STATUS_BADGE.issued;
  const ds = DOC_BADGE[inv.document_type] || DOC_BADGE.factura;
  const ps = PAY_BADGE[inv.payment_status] || PAY_BADGE.unpaid;
  const dlv = DELIVERY_BADGE[inv.delivery_status];

  return (
    <Sheet open={!!invoice} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="!max-w-3xl sm:!max-w-3xl flex flex-col" data-testid="invoice-detail-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            <FileText className="h-4 w-4 text-primary" />
            <span className="font-mono">{inv.series_name}{inv.number}</span>
            <Badge variant="outline" className={`text-[10px] ${ds.class}`}>{ds.label}</Badge>
            <Badge variant="outline" className={`text-[10px] ${ss.class}`}>{ss.label}</Badge>
            <Badge variant="outline" className={`text-[10px] ${ps.class}`}>{ps.label}</Badge>
          </SheetTitle>
          <SheetDescription className="space-y-0.5">
            <div>{inv.customer_name} {inv.customer_email && <span className="text-muted-foreground">· {inv.customer_email}</span>}</div>
            {inv.shopify_order_number && <div>Comandă Shopify: <Link to={`/comenzi/${encodeURIComponent(inv.shopify_order_id || inv.shopify_order_number)}`} className="font-mono text-primary underline">#{inv.shopify_order_number}</Link></div>}
          </SheetDescription>
        </SheetHeader>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <Cell label="Net" value={ron(inv.total_net, inv.currency)} />
          <Cell label="TVA" value={ron(inv.total_vat, inv.currency)} />
          <Cell label="Total" value={ron(inv.total_gross, inv.currency)} bold />
        </div>

        {/* Live SmartBill amounts (after refresh) */}
        {inv.smartbill_last_synced_at && (
          <div className="grid grid-cols-3 gap-3 mt-2 text-xs">
            <Cell label="Achitat (SmartBill)" value={ron(inv.smartbill_paid_amount, inv.currency)} small />
            <Cell label="Neachitat (SmartBill)" value={ron(inv.smartbill_unpaid_amount, inv.currency)} small />
            <Cell label="Sync" value={fmtDate(inv.smartbill_last_synced_at)} small mono />
          </div>
        )}

        {/* Delivery & order info */}
        {(inv.awb || inv.delivery_status || inv.shopify_order_id) && (
          <div className="mt-3 p-3 rounded-lg border border-border bg-secondary/30">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1">
              <Truck className="h-3 w-3" /> Livrare & corelare
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <KV label="AWB" value={inv.awb || "—"} mono />
              <KV label="Curier" value={inv.courier || "—"} />
              <KV label="Status livrare" value={dlv ? <Badge variant="outline" className={`text-[10px] ${dlv.class}`}>{dlv.label}</Badge> : "—"} />
              <KV label="Status decontare" value={inv.payment_status === "paid" ? <span className="text-emerald-700">Decontat</span> : <span className="text-amber-700">În așteptare</span>} />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <Button onClick={refresh} disabled={refreshing} size="sm" data-testid="invoice-refresh-status">
            {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            Refresh status SmartBill
          </Button>
          {pdfUrl && (
            <a href={pdfUrl} download={`${inv.series_name}${inv.number}.pdf`}>
              <Button variant="outline" size="sm" data-testid="invoice-download-pdf"><Download className="h-3.5 w-3.5 mr-1" /> Descarcă PDF</Button>
            </a>
          )}
          <a href={smartbillWebUrl(inv)} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" data-testid="invoice-open-smartbill">
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Deschide în SmartBill
            </Button>
          </a>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange?.(false)} className="ml-auto"><X className="h-3.5 w-3.5" /></Button>
        </div>

        {/* PDF preview */}
        <div className="flex-1 mt-4 border border-border rounded-lg overflow-hidden bg-zinc-50">
          {pdfLoading ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Se încarcă PDF-ul SmartBill…</div>
          ) : pdfUrl ? (
            <iframe title="invoice pdf" src={pdfUrl} className="w-full h-full" data-testid="invoice-pdf-iframe" />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-rose-600 gap-2"><AlertCircle className="h-4 w-4" /> PDF indisponibil (factură importată din XLSX sau token SmartBill expirat).</div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Cell({ label, value, bold, small, mono }) {
  return (
    <div className="rounded-lg border border-border bg-white p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`mt-1 tabular-nums ${bold ? "font-display text-xl font-bold" : small ? "text-xs" : "text-sm"} ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
